import { updateLastVisitedUrl } from "../services/user.service";

/**
 * Post-login redirect handling.
 * - Pending redirect: a target captured while logged out (deep link / apply click). One-time use, expires.
 * - Last visited url: persisted per person in the backend (person.last_visited_url).
 */
const PENDING_KEY = 'pendingRedirect';
const PENDING_TTL_MS = 15 * 60 * 1000;
const DEFAULT_ROUTE = '/home';
const NON_TRACKABLE_PATHS = ['/', '/login', '/register', '/authenticate'];

// Legacy key that was shared between users
try { localStorage.removeItem('redirectUrl'); } catch (e) { }

export const isTrackablePath = (url: string) => {
    if (!url || !url.startsWith('/') || url.startsWith('//')) return false;
    const pathname = url.split(/[?#]/)[0];
    return !NON_TRACKABLE_PATHS.includes(pathname) && !pathname.startsWith('/public/');
}

export const setPendingRedirect = (url: string) => {
    if (!isTrackablePath(url)) return;
    try {
        localStorage.setItem(PENDING_KEY, JSON.stringify({ url, ts: Date.now() }));
    } catch (e) { }
}

export const clearPendingRedirect = () => {
    try { localStorage.removeItem(PENDING_KEY); } catch (e) { }
    resolved = { personId: '', url: '', ts: 0 };
}

// PublicRoute and the login handlers can both resolve the redirect during the same login;
// remember the result briefly so they agree after the pending value has been consumed.
const RESOLVED_TTL_MS = 10 * 1000;
let resolved = { personId: '', url: '', ts: 0 };

const consumePendingRedirect = (): string => {
    try {
        const raw = localStorage.getItem(PENDING_KEY);
        localStorage.removeItem(PENDING_KEY);
        if (!raw) return '';
        const { url, ts } = JSON.parse(raw);
        return Date.now() - ts < PENDING_TTL_MS && isTrackablePath(url) ? url : '';
    } catch (e) {
        return '';
    }
}

/**
 * Where to send a person after login: fresh pending redirect, else their own last visited url, else home.
 */
export const getPostLoginRedirect = (person?: any): string => {
    const personId = person && person._id ? String(person._id) : '';
    if (resolved.url && resolved.personId === personId && Date.now() - resolved.ts < RESOLVED_TTL_MS) {
        return resolved.url;
    }
    const lastVisited = person && person.last_visited_url;
    const url = consumePendingRedirect() || (isTrackablePath(lastVisited) ? lastVisited : DEFAULT_ROUTE);
    resolved = { personId, url, ts: Date.now() };
    return url;
}

let lastSent = '';
let timer: ReturnType<typeof setTimeout> | undefined;
let queued: { personId: string, url: string } | undefined;

const sendQueued = () => {
    if (timer) clearTimeout(timer);
    timer = undefined;
    if (!queued) return;
    const { personId, url } = queued;
    queued = undefined;
    const key = `${personId}|${url}`;
    if (key === lastSent) return;
    lastSent = key;
    updateLastVisitedUrl(personId, url).catch(() => { lastSent = ''; });
}

/**
 * Persist the current url as the person's last visited url (debounced, skips duplicates).
 */
export const trackLastVisited = (personId: string, url: string) => {
    if (!personId || !isTrackablePath(url)) return;
    queued = { personId, url };
    if (timer) clearTimeout(timer);
    timer = setTimeout(sendQueued, 1000);
}

/** Send any queued save right away, e.g. on logout before the session ends. */
export const flushTrackLastVisited = () => {
    sendQueued();
    lastSent = '';
}
